import React, { useState } from 'react';
import { ButtonGroup, Button, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem, Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, Progress } from 'reactstrap';
import { exportToCSV, exportToExcel, exportToPDF, exportFormattedData, batchExport } from '../utils/exportData';

const ExportButton = ({ 
  data, 
  filename = 'export', 
  columns, 
  buttonText = 'Export',
  buttonSize = 'sm',
  buttonColor = 'secondary',
  showFormats = ['csv', 'excel', 'pdf'],
  onExport,
  enableBatch = false,
  batchSize = 1000,
  fetchAllData // Function to fetch all data for export
}) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState('csv');
  const [exportProgress, setExportProgress] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const [dataToExportModal, setDataToExport] = useState(false);
  const [setIsExportingModal, seIsExportingModal] = useState(false);

  const handleExport = async (format) => {
    setIsExporting(true);
    const exportFilename = `${filename}-${new Date().toISOString().split('T')[0]}`;

    try {
      // Fetch all data if fetchAllData function is provided
      let dataToExport = data;
      if (fetchAllData) {
        const allData = await fetchAllData();
        if (allData && allData.length > 0) {
          dataToExport = allData;
        }
      }

      if (!dataToExport || dataToExport.length === 0) {
        setDataToExport(true);
        return;
        console.log('Exporting data...');
      }

      if (enableBatch && dataToExport.length > batchSize) {
        await batchExport(dataToExport, format, {
          filename: exportFilename,
          batchSize,
          onProgress: ({ percentage }) => setExportProgress(percentage)
        });
      } else if (columns) {
        const extension = format === 'excel' ? 'xlsx' : format;
        exportFormattedData(dataToExport, columns, format, `${exportFilename}.${extension}`);
      } else {
        switch (format) {
          case 'csv':
            exportToCSV(dataToExport, `${exportFilename}.csv`);
            break;
          case 'excel':
            exportToExcel(dataToExport, `${exportFilename}.xlsx`);
            break;
          case 'pdf':
            exportToPDF(dataToExport, `${exportFilename}.pdf`, filename);
            break;
        }
      }

      if (onExport) onExport(format);
    } catch (error) {
      console.error('Export error:', error);
      seIsExportingModal(true);
    } finally {
      setIsExporting(false);
      setExportProgress(0);
    }
  };

  const formatIcons = {
    csv: 'fa-file-text-o',
    excel: 'fa-file-excel-o',
    pdf: 'fa-file-pdf-o'
  };

  // Single button mode
  if (showFormats.length === 1) {
    return (
      <Button 
        size={buttonSize} 
        color={buttonColor}
        onClick={() => handleExport(showFormats[0])}
        disabled={!data || data.length === 0 || isExporting}
      >
        <i className={`fa ${formatIcons[showFormats[0]] || 'fa-download'} mr-1`}></i>
        {buttonText}
      </Button>
    );
  }

  // Dropdown mode for multiple formats
  return (
    <>
      <UncontrolledDropdown>
        <DropdownToggle 
          size={buttonSize} 
          color={buttonColor} 
          caret
          disabled={!data || data.length === 0 || isExporting}
        >
          <i className="fa fa-download mr-1"></i>
          {buttonText}
        </DropdownToggle>
        <DropdownMenu right>
          {showFormats.includes('csv') && (
            <DropdownItem onClick={() => handleExport('csv')}>
              <i className="fa fa-file-text-o mr-2"></i>Export as CSV
            </DropdownItem>
          )}
          {showFormats.includes('excel') && (
            <DropdownItem onClick={() => handleExport('excel')}>
              <i className="fa fa-file-excel-o mr-2"></i>Export as Excel
            </DropdownItem>
          )}
          {showFormats.includes('pdf') && (
            <DropdownItem onClick={() => handleExport('pdf')}>
              <i className="fa fa-file-pdf-o mr-2"></i>Export as PDF
            </DropdownItem>
          )}
          {enableBatch && data && data.length > 1000 && (
            <>
              <DropdownItem divider />
              <DropdownItem onClick={() => setShowModal(true)}>
                <i className="fa fa-cog mr-2"></i>Advanced Export...
              </DropdownItem>
            </>
          )}
        </DropdownMenu>
      </UncontrolledDropdown>

      {/* Advanced Export Modal */}
      <Modal isOpen={showModal} toggle={() => setShowModal(false)}>
        <ModalHeader toggle={() => setShowModal(false)}>
          Advanced Export Options
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label>Export Format</Label>
              <Input type="select" value={selectedFormat} onChange={(e) => setSelectedFormat(e.target.value)}>
                {showFormats.includes('csv') && <option value="csv">CSV (.csv)</option>}
                {showFormats.includes('excel') && <option value="excel">Excel (.xlsx)</option>}
                {showFormats.includes('pdf') && <option value="pdf">PDF (.pdf)</option>}
              </Input>
            </FormGroup>
            
            <FormGroup>
              <Label>Data Information</Label>
              <p className="text-muted mb-0">
                Total Records: {data?.length || 0}<br />
                {data?.length > 1000 && `This will be exported in batches of ${batchSize} records`}
              </p>
            </FormGroup>

            {isExporting && (
              <FormGroup>
                <Label>Export Progress</Label>
                <Progress value={exportProgress} />
                <p className="text-center mt-2">{exportProgress}%</p>
              </FormGroup>
            )}
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button 
            color="primary" 
            onClick={() => {
              handleExport(selectedFormat);
              setShowModal(false);
            }}
            disabled={isExporting}
          >
            {isExporting ? 'Exporting...' : 'Export'}
          </Button>
        </ModalFooter>
      </Modal>


      <Modal isOpen={dataToExportModal} toggle={() => setDataToExport(false)} className="main-modal new-user-modal create-property-modal modal-dialog-centered import-details-modal">
        <ModalBody>
          <div className="data-found">
              <h5>No data to export</h5>
          </div>
        </ModalBody>
        <ModalFooter>
          <button className="btn-style-new" color="secondary" onClick={() => setDataToExport(false)}>
            Close
          </button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={setIsExportingModal} toggle={() => seIsExportingModal(false)} className="main-modal new-user-modal create-property-modal modal-dialog-centered import-details-modal">
        <ModalBody>
          <div className="data-found">
              <h5>Failed to export data</h5>
          </div>
        </ModalBody>
        <ModalFooter>
          <button className="btn-style-new" color="secondary" onClick={() => seIsExportingModal(false)}>
            Close
          </button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default ExportButton;